import cn from 'classnames'
import {FC, memo, useState} from 'react'

import {IpOfficePriceIncreaseAlert} from '@/components/alerts/IpOfficePriceIncreaseAlert'
import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {ChevronIcon} from '@/components/common/icons/ChevronIcon'
import {LocalizedNumberFormat} from '@/components/common/LocalizedNumberFormat'
import {EXPAND_TRANSITION} from '@/theme/cssTransitions'
import {theme} from '@/theme/theme'
import {Currency} from '@/utils/currency'
import {IpOfficePriceIncreaseAlertType} from '@/utils/registration-request/types'

type FormSummaryMobileDropdownProps = {
  title: string
  price: number
  isExpanded: boolean
  onClick: () => void
  currency: Currency
  hasDecimal?: boolean
  priceNote?: string
  ipOfficePriceIncreaseInfo?: IpOfficePriceIncreaseAlertType[]
}

export const FormSummaryMobileDropdown: FC<FormSummaryMobileDropdownProps> = memo(
  ({
    title,
    price,
    isExpanded,
    onClick,
    currency,
    hasDecimal = true,
    priceNote,
    ipOfficePriceIncreaseInfo,
  }) => {
    const [closedAlerts, setClosedAlerts] = useState<string[]>([])

    return (
      <>
        <button className="dropdown" type="button" onClick={onClick}>
          <Text variant={TextVariant.h5} element="span">
            {title}
          </Text>
          <div className="wrapper">
            <Text fontWeight={TextFontWeight.bold}>
              <LocalizedNumberFormat
                value={price}
                currency={currency}
                type="currency"
                hasDecimal={hasDecimal && price > 0}
              />
              {priceNote && (
                <Text variant={TextVariant.caption} fontWeight={TextFontWeight.regular}>
                  {priceNote}
                </Text>
              )}
            </Text>
            <div className={cn('chevron-wrapper', isExpanded && 'active')}>
              <ChevronIcon />
            </div>
          </div>
        </button>
        {ipOfficePriceIncreaseInfo?.map((info) => {
          const jurisdiction = info.jurisdiction
          const isClosed = closedAlerts.includes(jurisdiction)

          if (isClosed) return null

          return (
            <div key={jurisdiction} className="alert-wrapper">
              <IpOfficePriceIncreaseAlert
                {...info}
                handleClose={() =>
                  setClosedAlerts((prev) =>
                    prev.includes(jurisdiction) ? prev : [...prev, jurisdiction],
                  )
                }
              />
            </div>
          )
        })}

        <style jsx>{`
          .dropdown {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            padding: ${theme.spacing[3]} 0;
          }
          .wrapper {
            display: flex;
            align-items: center;
            justify-content: space-between;
          }
          .chevron-wrapper {
            margin-left: ${theme.spacing[1]};
            transition: transform ${EXPAND_TRANSITION}ms ease-in-out;
          }
          .active {
            transform: rotate(180deg);
          }
          .alert-wrapper {
            padding-bottom: ${theme.spacing[3]};
          }
        `}</style>
      </>
    )
  },
)
